/**
 * UI routes (React Router) + API routes (gateway).
 *
 * Per Kiran 2026-05-15 (SOP 15 Ch.15): only routes for SHIPPED features
 * live here. The pre-2026-05-15 file declared ~25 placeholder routes
 * for future phases — all removed alongside their D1 tables. Each
 * future phase adds its routes next to the new page/handler, not in
 * advance.
 *
 * Both maps hold templates with `:param` segments (e.g. `/orders/:id`).
 * Use `fillPath(APIROUTES.X, { id })` to substitute params at call time
 * so paths never get hand-concatenated elsewhere — Rule #13 (no
 * hardcoding).
 *
 * API paths are scoped to the api subdomain (api.uyyamadilu.com) and
 * therefore do NOT carry a leading `/api/` segment — the subdomain is
 * already the namespace. The UI calls them as
 * `${APIBASE}${APIROUTES.X}` where APIBASE is 'https://api.uyyamadilu.com'.
 */

/**
 * Substitute `:param` placeholders in a path template with values.
 * Values are encodeURIComponent'd; unknown params are left as-is so a
 * missing key produces an obvious bug, not a silent corruption.
 */
export function fillPath(template: string, params: Record<string, string>): string {
  let out = template
  for (const [k, v] of Object.entries(params)) {
    out = out.replace(`:${k}`, encodeURIComponent(v))
  }
  return out
}

export const ROUTES = {
  // Public
  HOME: '/',
  SHOP: '/shop',
  // Build — parallel storefront seed for the future "select products →
  // publish sub-domain store" flow (2026-06-05). Same data as /shop but
  // a separate URL + API surface so the two diverge without coupling.
  BUILD: '/build',
  NOTFOUND: '/404',
  // Auth — unified login at /login serves both customer and admin
  LOGIN: '/login',
  ACCOUNTLOGIN: '/account/login',
  ACCOUNTREGISTER: '/account/register',
  // Account (session-protected; AccountLayout)
  ACCOUNT: '/account',
  ACCOUNTPROFILE: '/account/profile',
  ACCOUNTORDERS: '/account/orders',
  ACCOUNTORDERDETAIL: '/account/orders/:id',
  // Customer-facing returns + free-text testimonials (Kiran 2026-06-15).
  ACCOUNTRETURNS: '/account/returns',
  ACCOUNTTESTIMONY: '/account/testimony',
  // Dashboard (operator; DashboardShell)
  DASHBOARD: '/dashboard',
  DASHBOARDTRAFFIC: '/dashboard/traffic',
  // IAM (top-level section under DashboardShell)
  IAM: '/iam',
  IAMUSERTYPES: '/iam/user-types',
  IAMSCREENACTIONS: '/iam/screen-actions',
  IAMGROUPS: '/iam/groups',
  IAMSTAFF: '/iam/staff',
  // Preferences domain — admin-only / cross-cutting screens. Settings
  // + Email Templates live here. Renamed 2026-05-19 (was "System") with
  // the move of the API surface to its own preferences
  // worker; see SOP 15 Ch.6 dashboard URL convention.
  PREFERENCESSETTINGS: '/preferences/settings',
  PREFERENCESEMAILTEMPLATES: '/preferences/email-templates',
  PREFERENCESWEBHOOKPROVIDERS: '/preferences/webhook-providers',
  PREFERENCESEMAILTEMPLATEEDIT: '/preferences/email-templates/:code',
  // Public staff welcome — token URL emailed at staff creation
  STAFFWELCOME: '/staff/welcome',
  // Manage (operator-only CRUD on operational lookups + entities).
  // Per SOP 15 Ch.6 §"Dashboard URL convention": /<domain>/<screen>
  // shape — no /dashboard/ prefix.
  MANAGEATTRIBUTES: '/manage/attributes',
  MANAGECUSTOMDATATYPES: '/manage/custom-data-types',
  MANAGECATEGORIES: '/manage/categories',
  MANAGEPURCHASES: '/manage/purchases',
  MANAGEGSTRATES: '/manage/gst-rates',
  MANAGEUNITS: '/manage/units',
  MANAGESTORES: '/manage/stores',
  MANAGEORGANISATIONS: '/manage/organisations',
  MANAGEPRODUCTSUPPLIERS: '/manage/product-suppliers',
  MANAGEMATERIALSUPPLIERS: '/manage/material-suppliers',
  // Products domain — promoted out of Manage 2026-05-17 (own worker
  // + nav group). Main catalog uses /products/list so URL shape stays
  // uniform with the sibling /products/pricing screen.
  PRODUCTS: '/products/list',
  PRODUCTVARIANTPRICING: '/products/variant-pricing',
  PRODUCTVARIANTPRICINGHISTORY: '/products/variant-pricing-history',
  // Per-variant raw-material mapping — moved here from Manufacturing 2026-05-23.
  PRODUCTPRICING: '/products/pricing',
  // Per-product composition links — for a given product, declare its
  // sub-products (2026-06-11). Operator screen under Product group.
  PRODUCTCOMPONENTS: '/products/components',
  // Solutions — customer-facing bundles of variants (2026-06-14).
  SOLUTIONS: '/products/solutions',
  // Per-variant Make/Buy source + finished-good supplier pricing (2026-05-25).
  PRODUCTSOURCE: '/products/source',
  // Customers domain — read-only operator screens. Data lives in
  // iam_users (user_type_code='customer'); the IAM worker hosts the API.
  CUSTOMERSREGISTERED: '/customers/registered',
  // Manufacturing domain — supplier + raw-material catalogues
  // (more screens land in subsequent phases: raw material pricing,
  // product↔raw-material mapping). Hosted on the manage worker.
  MANAGESUPPLIERS: '/manage/suppliers',
  MANUFACTURINGRAWMATERIALS: '/manufacturing/raw-materials',
  MANUFACTURINGPRICING: '/manufacturing/pricing',
  // Manufacturing — Material Purchases (Receive/View on raw materials,
  // 2026-06-04). Replaces the retired Manufacturing > Purchases screen.
  MANUFACTURINGMATERIALPURCHASES: '/manufacturing/material-purchases',
  // Manufacturing — Raw Material Stock History (per-material ledger;
  // mirror of warehouse.variant-stock-history).
  MANUFACTURINGRAWMATERIALSTOCKHISTORY: '/manufacturing/raw-material-stock-history',
  // Warehouse domain — per-variant stock movement ledger. Stock Entry
  // (the standalone manual append path) was retired 2026-06-03;
  // movements now ride on the Receive flow inside Product Purchases.
  WAREHOUSEPRODUCTSTOCKHISTORY: '/warehouse/product-stock-history',
  WAREHOUSEVARIANTSTOCKHISTORY: '/warehouse/variant-stock-history',
  WAREHOUSEPRODUCTPURCHASES: '/warehouse/product-purchases',
  // Order Management — operator screens (2026-06-05). Read-only in v1.
  ORDERMANAGEMENTORDERS: '/order-management/orders',
  ORDERMANAGEMENTRETURNS: '/order-management/returns',
  /** Operator review of customer build requests + Build click
   *  (Phase 2 of the multi-tenant pipeline, Kiran 2026-06-07). */
  ORDERMANAGEMENTBUILDREQUESTS: '/order-management/build-requests'
} as const

export const APIROUTES = {
  HEALTH: '/health',
  PING: '/ping',
  // Auth — unified /auth/login serves both customers and admins
  AUTHLOGIN: '/auth/login',
  AUTHREGISTER: '/auth/register',
  AUTHLOGOUT: '/auth/logout',
  // Session bootstrap — public, idempotent. Called by the SPA on mount so
  // every visitor (signed-in or not) has a server-side session id from the
  // first page load. Mints an anonymous record if no cookie is present.
  SESSIONINIT: '/session/init',
  // Email verification (signup flow, 2026-05-14)
  AUTHEMAILVERIFYREQUEST: '/auth/email/verify-request',
  AUTHEMAILVERIFYCONFIRM: '/auth/email/verify-confirm',
  // Auth — Staff welcome (token-based first-login password set)
  AUTHSTAFFWELCOMEVERIFY: '/auth/staff/welcome/verify',
  AUTHSTAFFWELCOMECONFIRM: '/auth/staff/welcome/confirm',
  // Account (session-protected)
  ACCOUNTME: '/account/me',
  // Self-service Profile screen (any signed-in user) — name + mobile +
  // address read/save, email change (verify-gated), password change.
  ACCOUNTPROFILE: '/account/profile',
  ACCOUNTEMAIL: '/account/email',
  ACCOUNTPASSWORD: '/account/password',
  // /account/addresses[/:id] retired 2026-06-04 — the address is
  // embedded in /account/profile (single D1-backed address). One
  // address source across cart + Profile overlay + history.
  ORDERS: '/orders',
  ORDERDETAIL: '/orders/:id',
  // Customer-side returns + testimonials (2026-06-15). Same auth
  // shape as /orders — session-protected, scoped to me.id server-side.
  ACCOUNTRETURNS: '/account/returns',
  ACCOUNTTESTIMONIALS: '/account/testimonials',
  // POST customer files a return on a delivered order (2026-06-17).
  ACCOUNTORDERRETURN: '/account/orders/:id/return',
  // POST customer cancels a not-yet-shipped order within
  // CANCEL.WINDOWDAYS (2026-06-19).
  ACCOUNTORDERCANCEL: '/account/orders/:id/cancel',
  ACCOUNTORDERHISTORY: '/account/orders/:id/history',
  // Dashboard landing — single entry point. Returns the signed-in
  // operator (who-am-I) + landing path, and is the place where
  // dashboard widget/data aggregations land in subsequent phases.
  // Hosted on the reports worker (2026-05-19 rebuild —
  // was /dashboard/me on the IAM worker). Logout was unified into
  // /auth/logout in the same change.
  DASHBOARD: '/dashboard',
  // Storefront visitor analytics — one screen, 10 endpoints (Kiran 2026-07-02).
  TRAFFICOVERVIEW: '/reports/traffic/overview',
  TRAFFICATTRIBUTION: '/reports/traffic/attribution',
  TRAFFICREALTIME: '/reports/traffic/realtime',
  TRAFFICFUNNEL: '/reports/traffic/funnel',
  TRAFFICPAGES: '/reports/traffic/pages',
  TRAFFICGEO: '/reports/traffic/geo',
  TRAFFICDEVICES: '/reports/traffic/devices',
  TRAFFICCOHORTS: '/reports/traffic/cohorts',
  TRAFFICFRAUD: '/reports/traffic/fraud',
  TRAFFICSESSIONS: '/reports/traffic/sessions',
  TRAFFICSESSIONDETAIL: '/reports/traffic/sessions/:sessionId',
  // IAM — User Types CRUD
  IAMUSERTYPES: '/iam/user-types',
  IAMUSERTYPEITEM: '/iam/user-types/:code',
  // IAM — Screen Actions (Layer 2a) — read-only catalogue
  IAMSCREENACTIONS: '/iam/screen-actions',
  // IAM — Groups (Layer 2c)
  IAMGROUPS: '/iam/groups',
  IAMGROUPITEM: '/iam/groups/:id',
  // Preferences — admin-editable runtime tunables + email-template
  // bodies. Renamed 2026-05-19 from /iam/system-config and
  // /iam/notification-templates; served by the new
  // preferences worker.
  PREFERENCESEMAILTEMPLATES: '/preferences/email-templates',
  PREFERENCESEMAILTEMPLATEITEM: '/preferences/email-templates/:code',
  PREFERENCESCONFIG: '/preferences/config',
  PREFERENCESCONFIGITEM: '/preferences/config/:key',
  // Webhook providers — per-provider event-name → OrderStatus mapping
  // + HMAC signing secret (Kiran 2026-06-18). KV-backed via the manage
  // worker; Layer-2c gated by screens.code='preferences.webhook-providers'.
  PREFERENCESWEBHOOKPROVIDERS: '/preferences/webhook-providers',
  PREFERENCESWEBHOOKPROVIDERITEM: '/preferences/webhook-providers/:provider',
  PREFERENCESWEBHOOKPROVIDERSECRET: '/preferences/webhook-providers/:provider/secret',
  // IAM — Staff CRUD (Layer-2c gated)
  IAMSTAFF: '/iam/staff',
  IAMSTAFFITEM: '/iam/staff/:id',
  // IAM — Registered customers (read-only). Layer-2c gated via
  // screens.code='customers.registered'. Same worker / table as
  // /iam/staff — kind filter differentiates.
  IAMCUSTOMERS: '/iam/customers',
  IAMCUSTOMERSITEM: '/iam/customers/:id',
  // Manage — operational/management endpoints (Manage worker)
  // Layer-2c gated via screens.code='manage.datatypes'. No UI.
  MANAGEDATATYPES: '/manage/datatypes',
  // Manage — attribute definitions (full CRUD). Layer-2c gated via
  // screens.code='manage.attributes'.
  MANAGEATTRIBUTES: '/manage/attributes',
  MANAGEATTRIBUTEITEM: '/manage/attributes/:id',
  // Manage — custom data types (user-defined enumerations).
  // Type-level + value-level CRUD; Layer-2c gated via
  // screens.code='manage.custom-data-types'.
  MANAGECUSTOMDATATYPES: '/manage/custom-data-types',
  MANAGECUSTOMDATATYPEITEM: '/manage/custom-data-types/:id',
  MANAGECUSTOMDATATYPEVALUES: '/manage/custom-data-types/:id/values',
  MANAGECUSTOMDATATYPEVALUEITEM: '/manage/custom-data-type-values/:id',
  // Manage — categories (entity + M:N attribute picker). Layer-2c
  // gated via screens.code='manage.categories'.
  MANAGECATEGORIES: '/manage/categories',
  MANAGECATEGORYITEM: '/manage/categories/:id',
  // Manufacturing — supplier catalogue (full CRUD). Hosted on the
  // manage worker. Layer-2c gated via screens.code='manage.suppliers'.
  MANAGESUPPLIERS: '/manage/suppliers',
  MANAGESUPPLIERITEM: '/manage/suppliers/:id',
  // Manage — bare purchase ledger (no supplier/raw_material/product
  // link). Writes to the existing `purchases` table but skips all 3
  // satellites; list filters to rows with NO satellite presence so
  // manufacturing/warehouse purchases stay invisible here. Layer-2c
  // gated via screens.code='manage.purchases'. Hosted on manage worker.
  MANAGEPURCHASES: '/manage/purchases',
  MANAGEPURCHASEITEM: '/manage/purchases/:id',
  MANAGEPURCHASEBILL: '/manage/purchases/:id/bill',
  // Manage — GST rate catalog. Add / set-current / soft-delete only
  // (rates are immutable once added — add a new row for a new rate).
  // The current rate auto-applies to every new purchase entry across
  // all 3 purchase screens. Layer-2c gated via screens.code='manage.gst-rates'.
  MANAGEGSTRATES: '/manage/gst-rates',
  MANAGEGSTRATEITEM: '/manage/gst-rates/:id',
  MANAGEGSTRATESETCURRENT: '/manage/gst-rates/:id/set-current',
  // Manage — units catalog. Add / soft-delete only (immutable). The
  // unit name string is snapshotted onto every row that references
  // a unit (raw_materials.unit_label, purchases.unit_label, etc.) so
  // delisting a unit doesn't retroactively touch historical data.
  MANAGEUNITS: '/manage/units',
  MANAGEUNITITEM: '/manage/units/:id',
  // Manage — stores catalog. Add / edit / soft-delete on non-default
  // rows; default row (is_default = 1, slug='uyyamadilu') refuses
  // edit + delete at the repo level. Layer-2c gated via
  // screens.code='manage.stores'.
  MANAGESTORES: '/manage/stores',
  MANAGESTOREITEM: '/manage/stores/:id',
  // Manage — organisations catalog. Full CRUD; each org carries 1..N
  // postal addresses (HQ + branches) via organisation_addresses link.
  // Layer-2c gated via screens.code='manage.organisations'.
  MANAGEORGANISATIONS: '/manage/organisations',
  MANAGEORGANISATIONITEM: '/manage/organisations/:id',
  // Manage — product↔supplier link catalog. Add + soft-delete; no
  // edit (a link is just a (product, supplier) pair; change means
  // delete + add). Layer-2c gated via screens.code='manage.product-suppliers'.
  MANAGEPRODUCTSUPPLIERS: '/manage/product-suppliers',
  MANAGEPRODUCTSUPPLIERITEM: '/manage/product-suppliers/:id',
  // Manage — raw_material↔supplier link catalog. Same shape as
  // /manage/product-suppliers but for raw materials. Add + soft-delete;
  // no edit. Layer-2c gated via screens.code='manage.material-suppliers'.
  MANAGEMATERIALSUPPLIERS: '/manage/material-suppliers',
  MANAGEMATERIALSUPPLIERITEM: '/manage/material-suppliers/:id',
  // Manufacturing — raw materials catalogue (full CRUD + M:N supplier
  // picker). Layer-2c gated via screens.code='manufacturing.raw-materials'.
  MANUFACTURINGRAWMATERIALS: '/manufacturing/raw-materials',
  MANUFACTURINGRAWMATERIALITEM: '/manufacturing/raw-materials/:id',
  // Manufacturing — per-(raw_material, supplier) pricing.
  //   GET    /                                       list materials with supplier counts + price range
  //   GET    /:rawMaterialId/suppliers               drill-down: every active supplier price for one material
  //   POST   /:rawMaterialId/suppliers               add a new supplier price for this material
  //   PATCH  /:rawMaterialId/suppliers/:pricingId    edit one (material, supplier) row
  //   DELETE /:rawMaterialId/suppliers/:pricingId    soft-delete (flips is_active=0)
  // Layer-2c gated via screens.code='manufacturing.pricing'.
  MANUFACTURINGPRICING: '/manufacturing/pricing',
  MANUFACTURINGPRICINGSUPPLIERS: '/manufacturing/pricing/:id/suppliers',
  MANUFACTURINGPRICINGSUPPLIERITEM: '/manufacturing/pricing/:id/suppliers/:pricingId',
  // Product — per-variant raw-material mapping (moved out of Manufacturing
  // 2026-05-23; now owned by the PRODUCTS worker).
  //   GET                    paginated list of active variants with current mapping summary + baseline cost
  //   GET   /:variantId      detail: current links + raw-materials catalogue + per-rm supplier prices for picker
  //   PATCH /:variantId      replace mapping set (links only)
  //   POST  /:variantId/accept   copy the variant's cost onto it (enqueue rollup)
  //   POST  /:variantId/clone    clone materials + cost to sibling variants
  // Layer-2c gated via screens.code='product.product-pricing'.
  PRODUCTPRICING: '/product-pricing',
  PRODUCTPRICINGITEM: '/product-pricing/:id',
  PRODUCTPRICINGACCEPT: '/product-pricing/:id/accept',
  PRODUCTPRICINGCLONE: '/product-pricing/:id/clone',
  //   PATCH /:id/preview-cost   set a BUY variant's preview baseline cost
  //                             (typed; Make rejected — cost from materials)
  PRODUCTPRICINGPREVIEWCOST: '/product-pricing/:id/preview-cost',
  // Product Components — per-product composition links (migration 0117).
  // GET   /product-components?productId=...            list + pickers
  // POST  /product-components                          add link
  // PATCH /product-components/:parentId/:componentId   update qty/sort
  // DELETE /product-components/:parentId/:componentId  soft-delete link
  // Layer-2c gated via screens.code='product.product-components'.
  PRODUCTCOMPONENTSAPI: '/product-components',
  PRODUCTCOMPONENTSITEM: '/product-components/:parentId/:componentId',
  // Solutions — customer-facing bundles of variants (migration 0118).
  //   GET    /solutions                      — paginated list
  //   POST   /solutions                      — create (multipart: solution JSON + hero image file)
  //   GET    /solutions/variants-picker      — variant picker for the modal
  //   GET    /solutions/:id                  — detail
  //   PATCH  /solutions/:id                  — update (multipart: solution JSON + optional image)
  //   DELETE /solutions/:id                  — soft-delete (cascades to media + variants)
  // Layer-2c gated via screens.code='product.solutions'.
  SOLUTIONS: '/solutions',
  SOLUTIONITEM: '/solutions/:id',
  SOLUTIONVARIANTSPICKER: '/solutions/variants-picker',
  // Manufacturing — Material Purchases (view-only allocation ledger,
  // 2026-06-04). The Receive flow snapshots a slice of a bare Manage
  // Purchase across N raw materials linked to the supplier. Mirror of
  // /warehouse/product-purchases. Layer-2c gated via
  // screens.code='manufacturing.material-purchases'.
  MANUFACTURINGMATERIALPURCHASES: '/manufacturing/material-purchases',
  MANUFACTURINGMATERIALPURCHASEALLOCATE: '/manufacturing/material-purchases/allocate',
  MANUFACTURINGMATERIALPURCHASESBYMATERIAL: '/manufacturing/material-purchases/by-material',
  MANUFACTURINGMATERIALRECEIVEOPTIONS: '/manufacturing/material-purchases/by-material/:materialId/receive-options',
  // Manufacturing — Raw Material Stock History (read-only ledger +
  // current-year chart; mirror of /warehouse/variant-stock-history).
  // Layer-2c gated via screens.code='manufacturing.raw-material-stock-history'.
  // Ledger reads on MANUFACTURING; /current-year aggregates on REPORTS,
  // same split as the warehouse history pair.
  MANUFACTURINGRAWMATERIALSTOCKHISTORY: '/manufacturing/raw-material-stock-history',
  MANUFACTURINGRAWMATERIALSTOCKCURRENTYEAR: '/manufacturing/raw-material-stock-history/current-year',
  // Products — catalogue rows + category-derived attribute values via
  // two link tables. Layer-2c gated via screens.code='products'.
  // Owned by the products worker (own deployable).
  // DELETE retired 2026-05-19 — replaced by the explicit
  // /:id/decommission endpoint.
  PRODUCTS: '/products',
  PRODUCTITEM: '/products/:id',
  PRODUCTDECOMMISSION: '/products/:id/decommission',
  // Variant propagation — reconciles product_variants vs saved
  // is_axes=1 CDT selections. Idempotent. See IAMAUDITACTIONS.PRODUCTVARIANTSPROPAGATE.
  PRODUCTVARIANTSPROPAGATE: '/products/:id/variants/propagate',
  // Variant Pricing — per-product variant list (variant_id, product_name,
  // axes_label, baseline_cost, current selling price + Healthy/Bleeding
  // status) plus the inline selling-price edit.
  // Layer-2c gated via screens.code='product-variant-pricing'. Same worker
  // as Products.
  PRODUCTVARIANTPRICING: '/product-variant-pricing',
  // Set a variant's selling price (append snapshot).
  // gated via screens.code='product-variant-pricing' is_edit.
  PRODUCTVARIANTPRICINGSELLINGPRICE: '/product-variant-pricing/:id/selling-price',
  // Variant Pricing — per-variant pricing ledger.
  //   GET: products dropdown + paginated history for a selected product
  //   PATCH /:id: edit selling_price on the is_current row
  //   GET /current-year: monthly + quarterly avg baseline cost + avg selling price
  //                       buckets for the chart pair (Layer-2c reuses
  //                       product-variant-pricing-history.is_view)
  // Layer-2c gated via screens.code='product-variant-pricing-history'.
  PRODUCTVARIANTPRICINGHISTORY: '/product-variant-pricing-history',
  PRODUCTVARIANTPRICINGHISTORYCURRENTYEAR: '/product-variant-pricing-history/current-year',
  // Product Source — per-variant Make/Buy + finished-good supplier
  // pricing. Worker matches the sub-paths by prefix off this base.
  // Layer-2c gated via screens.code='product.source'. Same worker as Products.
  PRODUCTSOURCE: '/product-source',
  // Media — gateway-proxied byte delivery. Same worker as Products.
  // scope='any' middleware accepts EITHER a customer OR a dashboard
  // cookie (decision 9 of the product-media design); anonymous → 401.
  MEDIAITEM: '/media/:id',
  // Warehouse — per-variant stock movement ledger (migration 0064).
  // History reads on the WAREHOUSE worker; /current-year chart aggregates
  // on the REPORTS worker (same split as Variant Pricing). The standalone
  // Stock Entry endpoint was retired 2026-06-03 — see the Product Purchases
  // /receive endpoint for the only stock-in append path. Layer-2c gated
  // per screen below.
  WAREHOUSEPRODUCTSTOCKHISTORY: '/warehouse/product-stock-history',
  WAREHOUSEVARIANTSTOCKHISTORY: '/warehouse/variant-stock-history',
  WAREHOUSEPRODUCTSTOCKCURRENTYEAR: '/warehouse/product-stock-history/current-year',
  WAREHOUSEVARIANTSTOCKCURRENTYEAR: '/warehouse/variant-stock-history/current-year',
  // Warehouse — view-only allocation ledger for finished products
  // bought via Manage > Purchases. Edit / Delete / Bill image on wpp
  // rows were retired 2026-06-04 (canonical ledger is Manage). The
  // allocate path snapshots a bare Manage Purchase onto a product
  // (writes wpp row + variant_stock_movements atomically). Layer-2c
  // gated via screens.code='warehouse.product-purchases'.
  WAREHOUSEPRODUCTPURCHASES: '/warehouse/product-purchases',
  WAREHOUSEPRODUCTPURCHASEALLOCATE: '/warehouse/product-purchases/allocate',
  // Per-product aggregates for the screen's top summary table + the
  // product-scoped receive-options lookup (supplier + invoice + variant
  // pickers in one fetch). The Receive modal sits on a product row,
  // so receive options are product-scoped — there's no per-purchase
  // receive-options endpoint anymore.
  WAREHOUSEPRODUCTPURCHASESBYPRODUCT: '/warehouse/product-purchases/by-product',
  WAREHOUSEPRODUCTRECEIVEOPTIONS: '/warehouse/product-purchases/by-product/:productId/receive-options',
  // Storefront — PUBLIC, anonymous, read-only (authRequired:false, no
  // screen permission). Same worker as Products. SHOPPRODUCT assembles the
  // sellable product; SHOPMEDIA streams a product image to anonymous
  // shoppers (the authed /media/:id can't be used without a cookie).
  SHOPPRODUCT: '/shop/product',
  // Per-product detail by id (multi-product browse, 2026-06-05).
  SHOPPRODUCTITEM: '/shop/product/:id',
  // Paginated list with search + category + price + CDT filters.
  SHOPPRODUCTS: '/shop/products',
  // Sidebar seed (categories + catalog price range).
  SHOPFILTERMETA: '/shop/filter-meta',
  // Dynamic price range scoped by optional ?categoryIds=... — the
  // slider re-seeds when the customer changes the category selection.
  SHOPPRICERANGE: '/shop/price-range',
  // CDT values for the selected category set (refetched on category change).
  SHOPFILTERATTRIBUTES: '/shop/filter-attributes',
  SHOPMEDIA: '/shop/media/:id',
  /** Tenant resolver (Phase 3, Kiran 2026-06-07). PUBLIC, anonymous,
   *  read-only. SPA boot fetches `?host=<window.location.host>` and
   *  uses the result to render the tenant's name, scope the catalog,
   *  and decide whether operator routes are accessible. */
  TENANTSBYHOST: '/tenants/by-host',
  // Build — parallel surface to /shop/* (2026-06-05). Same source data,
  // separate URLs so the future store-builder selection + publish-sub-domain
  // flow can diverge cleanly.
  BUILDPRODUCT: '/build/product',
  BUILDPRODUCTITEM: '/build/product/:id',
  BUILDPRODUCTS: '/build/products',
  BUILDFILTERMETA: '/build/filter-meta',
  BUILDPRICERANGE: '/build/price-range',
  BUILDFILTERATTRIBUTES: '/build/filter-attributes',
  BUILDMEDIA: '/build/media/:id',
  // Build request submission (storefront customer Request flow,
  // 2026-06-06). PUBLIC route on the gateway but rejected by the
  // commerce handler when the visitor isn't a signed-in customer.
  // Atomic: snapshot the build cart into build_requests +
  // build_request_items, tear down ONLY the build lines.
  BUILDREQUESTSCONFIRM: '/build/requests/confirm',
  // Operator review surface for build requests (Phase 2, 2026-06-07).
  //   GET     /order-management/build-requests            paginated list with customer + org joins
  //   GET     /order-management/build-requests/:id        detail (header + items)
  //   PATCH   /order-management/build-requests/:id        { status?, organisation_id?, max_allowed_count?, max_cart_value_paisa? }
  //   PATCH   /order-management/build-requests/:id/items  { items: [...] } full line replace
  //   POST    /order-management/build-requests/:id/build  derive subdomain slug + flip status to 'built'
  // Layer-2c gated via screens.code='order-management.build-requests' (seeded by 0108).
  ORDERMANAGEMENTBUILDREQUESTS: '/order-management/build-requests',
  ORDERMANAGEMENTBUILDREQUESTITEM: '/order-management/build-requests/:id',
  ORDERMANAGEMENTBUILDREQUESTITEMS: '/order-management/build-requests/:id/items',
  ORDERMANAGEMENTBUILDREQUESTBUILD: '/order-management/build-requests/:id/build',
  /** Decommission: detach the CF custom domain so `<slug>.uyyamadilu.com`
   *  stops responding. D1 state is intentionally untouched (status
   *  stays 'built', subdomain bound, customers + carts + orders all
   *  preserved). audit_log entry is the source of truth that the
   *  URL was revoked. Kiran 2026-06-08. */
  ORDERMANAGEMENTBUILDREQUESTDECOMMISSION: '/order-management/build-requests/:id/decommission',
  /** Re-attach: idempotent PUT against CF for an already-built row
   *  whose custom-domain binding is missing (silent PUT failure,
   *  accidental detach). D1 state untouched; audit-only. Kiran
   *  2026-06-08 (RidgeGap incident). */
  ORDERMANAGEMENTBUILDREQUESTREATTACH: '/order-management/build-requests/:id/reattach',
  // Cart — PUBLIC, anonymous-friendly. Identity resolved server-side from
  // the session cookie (resolveCartOwner). Same worker as Products. The cart
  // tables are owned by satellites (guest_cart / customer_cart); no cart_id
  // on the wire — the SPA never needs to know the row id.
  CART: '/cart',
  CARTITEMS: '/cart/items',
  CARTITEM: '/cart/items/:variantId',
  // Customer clicks "Email me a quote" on /cart — renders PDF, stashes
  // in R2, enqueues a notification with operator BCC. Kiran 2026-06-28.
  CARTQUOTEEMAIL: '/cart/quote-email',
  // Storefront visitor telemetry — SPA fires a pageview event on every
  // route change, and a sendBeacon exit event on beforeunload. Both go
  // through TELEMETRY_QUEUE → telemetry-consumer → D1 + Supabase.
  // Kiran 2026-06-30.
  TELEMETRYPAGEVIEW: '/telemetry/pageview',
  TELEMETRYEXIT: '/telemetry/exit',
  // Order placement (storefront customer Confirm flow, 2026-06-04).
  // PUBLIC route on the gateway (no operator screen) but rejected by
  // the commerce handler when the visitor isn't a signed-in customer.
  // Atomic confirm: snapshot the cart into orders + order_items, then
  // tear down the cart. No payment, no stock decrement in v1.
  ORDERSCONFIRM: '/orders/confirm',
  // Order Management (operator) — read-only paginated lists.
  // Layer-2c gated via screens.code='order-management.{orders,returns}'.
  ORDERMANAGEMENTORDERS: '/order-management/orders',
  ORDERMANAGEMENTRETURNS: '/order-management/returns',
  // POST operator-driven status transitions (Kiran 2026-06-17).
  ORDERMANAGEMENTORDERTRANSITION: '/order-management/orders/:id/transition',
  ORDERMANAGEMENTRETURNTRANSITION: '/order-management/returns/:id/transition',
  ORDERMANAGEMENTORDERHISTORY: '/order-management/orders/:id/history'
} as const
